import { useState } from 'react';
import { Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useSoftDelete, type SoftDeleteEntity } from '@/api/mutations/delete';
import { cn } from '@/lib/cn';

interface DeleteButtonProps {
  entity: SoftDeleteEntity;
  id: string;
  label?: string;
  onDeleted?: () => void;
  className?: string;
  iconOnly?: boolean;
}

export function DeleteButton({
  entity,
  id,
  label = 'deletar',
  onDeleted,
  className,
  iconOnly = false,
}: DeleteButtonProps) {
  const del = useSoftDelete();
  const [confirming, setConfirming] = useState(false);

  async function exec() {
    try {
      await del.mutateAsync({ entity, id });
      toast.success(`${entity} arquivada`, {
        action: {
          label: 'desfazer',
          onClick: async () => {
            try {
              await del.mutateAsync({ entity, id, undo: true });
              toast.success('restaurada');
            } catch (e) {
              toast.error('Falha ao desfazer');
            }
          },
        },
      });
      onDeleted?.();
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    } finally {
      setConfirming(false);
    }
  }

  if (confirming) {
    return (
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={exec}
          disabled={del.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-destructive px-2 py-1 text-[11px] font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
        >
          <Check className="h-3 w-3" strokeWidth={2.5} />
          confirmar
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={del.isPending}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className={cn(
        'inline-flex items-center gap-1 rounded-md text-muted-foreground/60 transition-colors hover:bg-destructive/10 hover:text-destructive',
        iconOnly ? 'h-7 w-7 items-center justify-center' : 'px-2 py-1 text-[11px]',
        className,
      )}
      title={`Arquivar ${entity}`}
    >
      <Trash2 className="h-3 w-3" strokeWidth={2.25} />
      {!iconOnly && label}
    </button>
  );
}
